import { ReactElement, useState } from 'react';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import {
  getBgClassNames,
  getBorderClassNames,
  getGradientColor,
  getRoundedClassNames,
  getTextClassNames,
} from '@nx-frontend/shared/themes';
import { PortalClassState, PortalStyleConfig, ThemeColorSchemas } from '@nx-frontend/shared/types';
import { IconKey } from '@nx-frontend/shared/types/icon';
import { ThemeButtonColorConfig } from '@nx-frontend/shared/types/theme';
import { defaultButtonTheme, disabledButtonTheme, disabledStrokeButtonTheme } from './theme';

interface ActionButtonProps {
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  title?: string | false;
  icon?: IconKey;
  type?: 'fill' | 'stroke';
  size?: 'md' | 'sm' | 'lg';
  disabled?: boolean;
  theme?: ThemeButtonColorConfig;
  className?: string[];
  onClick?: (() => void) | (() => Promise<void>);
  href?: string;
  hint?: string;
  children?: ReactElement;
}

export const ActionButton: React.FC<ActionButtonProps> = ({
  title = false,
  icon,
  type = 'fill',
  size = 'md',
  className,
  disabled = false,
  themeColors,
  themeStyle,
  theme = defaultButtonTheme,
  onClick = () => null,
  href,
  hint,
  children,
}: ActionButtonProps) => {
  const [loading, setLoading] = useState(false);

  const disabledState = disabled || loading;

  const { text, bg } =
    disabledState === false ? theme : type === 'stroke' ? disabledStrokeButtonTheme : disabledButtonTheme;

  const themeClassName: string[][] = [getRoundedClassNames(themeStyle)];
  if (type === 'fill') {
    themeClassName.push(getTextClassNames(themeColors, text.default));
    themeClassName.push(getBgClassNames(themeColors, bg.default));
    if (!disabledState) {
      themeClassName.push(getBgClassNames(themeColors, bg.hover, [PortalClassState.HOVER]));
    }
  }
  if (type === 'stroke') {
    const [firstBgColor] = getGradientColor({ themeColors, color: bg.default });
    const [firstBgHoverColor] = getGradientColor({ themeColors, color: bg.hover });
    themeClassName.push(getTextClassNames(themeColors, [firstBgColor]));
    themeClassName.push(getBorderClassNames(themeColors, themeStyle, [firstBgColor]));
    if (!disabledState) {
      themeClassName.push(
        getBorderClassNames(themeColors, themeStyle, [firstBgHoverColor], {
          states: [PortalClassState.HOVER],
        })
      );
      themeClassName.push(['hover:bg-opacity-5'], getBgClassNames(themeColors, bg.hover, [PortalClassState.HOVER]));
    }
  }

  const handleClick = async (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabledState) {
      return;
    }
    setLoading(true);
    await onClick();
    setLoading(false);
    if (href != null) {
      window.open(href, href.startsWith('https://') || event.metaKey || event.ctrlKey ? '_blank' : '_self');
    }
  };

  return (
    <div
      className={classNames('inline-flex gap-2 items-center select-none', className, ...themeClassName, {
        'cursor-pointer': !disabledState,
        'p-2.5 text-sm font-bold': size === 'md',
        'p-1.5 text-sm font-bold': size === 'sm',
        'px-5': title,
      })}
      onClick={handleClick}
      title={hint}
      data-cy="action-button"
    >
      {title}
      {icon && <Icon icon={IconsMap[icon]} className="size-5" />}
      {children}
    </div>
  );
};
